import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getGradeAPI } from "../../../assignmentAPI";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import UserInputBar from "../../widgets/UserInputBar";
import CommonPage from "../../common/CommonPage";

const ComprehensivePage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [userInput, setUserInput] = useState("");
  const [userInputList, setUserInputList] = useState([]);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  // Shuffle array helper
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    let data = section.question.data;

    // Shuffle if random order
    if (section.question.is_random) {
      data = shuffleArray(data);
    }

    setPageList(
      data.map((question) => {
        if (question.type === "sentence:scramble") {
          return { ...question, input: [], isSubmitted: false, isCorrect: false };
        } else if (question.type === "sentence:blank") {
          const answerWords = question.answer ? question.answer.split(" / ").map(w => w.trim()) : [];
          return { ...question, input: Array(answerWords.length).fill(""), isSubmitted: false, isCorrect: false };
        } else {
          return { ...question, input: "", isSubmitted: false, isCorrect: false };
        }
      })
    );
    setCurrentIndex(0);
  }, [section]);

  useEffect(() => {
    if (currentPage.type === "sentence:scramble") {
      setUserInput("");
      setUserInputList([]);
    } else if (currentPage.type === "sentence:blank") {
      setUserInputList(currentPage.input || []);
      setUserInput("");
    } else {
      if (currentPage.isSubmitted) {
        if (currentPage.isCorrect) {
          setUserInput(currentPage.input);
        } else {
          setUserInput(currentPage.answer);
        }
      } else {
        setUserInput(currentPage.input || "");
      }
      setUserInputList([]);
    }
  }, [currentIndex]);

  const handleChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleBlankChange = (index, value) => {
    const newInputList = [...userInputList];
    newInputList[index] = value;
    setUserInputList(newInputList);

    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: newInputList,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleClickWord = (word, wordIndex) => {
    if (currentPage.isSubmitted) return;

    const prevInput = currentPage.input || [];
    const existingIndex = prevInput.findIndex((item) => item.index === wordIndex);

    let currentInput;
    if (existingIndex >= 0) {
      currentInput = prevInput.filter((item) => item.index !== wordIndex);
    } else {
      currentInput = [...prevInput, { word, index: wordIndex }];
    }

    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return {
          ...prevPage,
          input: currentInput,
        };
      }
      return prevPage;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (currentPage.isSubmitted) return;

    let isAnswerCorrect = false;
    let newPageList;

    if (currentPage.type === "sentence:blank") {
      // Blank: exact match validation
      const answerWords = currentPage.answer.split(" / ").map(w => w.trim());

      if (userInputList.some((input) => !input || input.trim() === "")) {
        return alert("모든 빈칸을 채워주세요.");
      }

      isAnswerCorrect = answerWords.every(
        (word, index) =>
          word.trim().toLowerCase() === userInputList[index].trim().toLowerCase()
      );

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            input: userInputList,
            isCorrect: isAnswerCorrect,
            isSubmitted: true,
          };
        }
        return page;
      });

      setOverlayVisible(true);
      setIsCorrect(isAnswerCorrect);
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));

      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
    } else if (currentPage.type === "sentence:scramble") {
      // Scramble: word order validation
      const input = currentPage.input || [];
      const match = currentPage.question.match(/\*\*\((.*?)\)\*\*/);
      if (!match) return;
      const scrambledWords = match[1].split(" / ").map((word) => word.trim());

      if (input.length < scrambledWords.length) {
        alert("모든 단어를 선택해주세요.");
        return;
      }

      const userSentence = input.map((item) => item.word).join(" ");
      const correctSentence = currentPage.answer;

      isAnswerCorrect =
        userSentence.trim().toLowerCase() === correctSentence.trim().toLowerCase();

      newPageList = pageList.map((page, index) => {
        if (index === currentIndex) {
          return {
            ...page,
            isSubmitted: true,
            isCorrect: isAnswerCorrect,
          };
        }
        return page;
      });

      setOverlayVisible(true);
      setIsCorrect(isAnswerCorrect);
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));

      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
    } else if (currentPage.type === "sentence:koreng") {
      // Composition: AI grading
      if (userInput === "") {
        return alert("답을 입력해주세요.");
      }

      const grade = await getGradeAPI({
        data: {
          kor: currentPage.question,
          input: userInput,
          answer: currentPage.answer,
        },
        type: "composition",
      });

      setOverlayVisible(true);
      setIsCorrect(grade.is_correct === 1);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            input: userInput,
            isCorrect: grade.is_correct === 1,
            isSubmitted: true,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));

      setTimeout(() => {
        setOverlayVisible(false);
        if (grade.is_correct === 0) {
          setUserInput(currentPage.answer);
        }
      }, 600);
    } else if (currentPage.type === "sentence:engkor") {
      // Translation: AI grading
      if (userInput === "") {
        return alert("답을 입력해주세요.");
      }

      const grade = await getGradeAPI({
        data: {
          eng: currentPage.question,
          input: userInput,
          answer: currentPage.answer,
        },
        type: "translation",
      });

      setOverlayVisible(true);
      setIsCorrect(grade.is_correct === 1);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            input: userInput,
            isCorrect: grade.is_correct === 1,
            isSubmitted: true,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));

      setTimeout(() => {
        setOverlayVisible(false);
        if (grade.is_correct === 0) {
          setUserInput(currentPage.answer);
        }
      }, 600);
    }
  };

  // Render question based on type
  const renderQuestion = () => {
    if (currentPage.type === "sentence:blank") {
      const getQuestionParts = () => {
        if (!currentPage.question) return [];
        return currentPage.question.split(/_{4,}/);
      };

      const parts = getQuestionParts();
      const answerWords = currentPage.answer ? currentPage.answer.split(" / ").map(w => w.trim()) : [];
      const displayInputs = currentPage.isSubmitted
        ? currentPage.isCorrect
          ? userInputList
          : answerWords
        : userInputList;

      return (
        <>
          <div style={{ fontSize: "20px" }}>빈칸에 알맞은 단어를 입력하시오.</div>
          <div
            style={{
              fontSize: "18px",
              textAlign: "center",
              lineHeight: "1.5",
              color: "#666",
            }}
          >
            {currentPage.hint}
          </div>
          <div style={{ fontSize: "24px", lineHeight: "2", textAlign: "center" }}>
            {parts.map((part, index) => (
              <span key={index}>
                {part}
                {index < answerWords.length && (
                  <input
                    type="text"
                    value={displayInputs[index] || ""}
                    onChange={(e) => handleBlankChange(index, e.target.value)}
                    disabled={currentPage.isSubmitted}
                    style={{
                      width: `${Math.max(100, answerWords[index].length * 12)}px`,
                      border: "none",
                      borderBottom: currentPage.isSubmitted
                        ? currentPage.isCorrect
                          ? "2px solid #4CAF50"
                          : "2px solid #f44336"
                        : "2px solid #333",
                      outline: "none",
                      textAlign: "center",
                      fontSize: "24px",
                      padding: "4px 8px",
                      margin: "0 4px",
                      backgroundColor: "transparent",
                    }}
                  />
                )}
              </span>
            ))}
          </div>
        </>
      );
    } else if (currentPage.type === "sentence:scramble") {
      const match = currentPage.question.match(/\*\*\((.*?)\)\*\*/);
      const scrambledWords = match ? match[1].split(" / ").map((word) => word.trim()) : [];

      const isWordSelected = (wordIndex) => {
        const input = currentPage.input || [];
        return input.some((item) => item.index === wordIndex);
      };

      const getWordSelectionNumber = (wordIndex) => {
        const input = currentPage.input || [];
        const index = input.findIndex((item) => item.index === wordIndex);
        return index >= 0 ? index + 1 : null;
      };

      return (
        <>
          <div style={{ fontSize: "20px" }}>
            주어진 단어를 올바른 순서로 배열하시오.
          </div>
          <div
            style={{
              fontSize: "18px",
              textAlign: "center",
              lineHeight: "1.5",
              color: "#666",
            }}
          >
            {currentPage.hint}
          </div>
          <div
            style={{
              minHeight: "60px",
              padding: "15px 20px",
              fontSize: "24px",
              borderBottom: "2px solid #333",
              width: "80%",
              textAlign: "center",
              lineHeight: "1.5",
            }}
          >
            {currentPage.input && currentPage.input.length > 0
              ? currentPage.input.map((item) => item.word).join(" ")
              : ""}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
              maxWidth: "700px",
            }}
          >
            {scrambledWords.map((word, index) => {
              const selected = isWordSelected(index);
              const selectionNumber = getWordSelectionNumber(index);

              return (
                <button
                  key={index}
                  onClick={() => handleClickWord(word, index)}
                  disabled={currentPage.isSubmitted}
                  style={{
                    padding: "12px 24px",
                    fontSize: "18px",
                    backgroundColor: selected ? "#4CAF50" : "#f0f0f0",
                    color: selected ? "#fff" : "#333",
                    border: selected ? "2px solid #4CAF50" : "2px solid #ddd",
                    borderRadius: "8px",
                    cursor: currentPage.isSubmitted ? "default" : "pointer",
                    fontWeight: selected ? "600" : "400",
                    position: "relative",
                    opacity: currentPage.isSubmitted ? 0.6 : 1,
                  }}
                >
                  {word}
                  {selected && selectionNumber && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        backgroundColor: "#fff",
                        color: "#4CAF50",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        border: "2px solid #4CAF50",
                      }}
                    >
                      {selectionNumber}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {currentPage.isSubmitted && !currentPage.isCorrect && (
            <div style={{ fontSize: "18px", color: "#d32f2f", textAlign: "center" }}>
              정답: {currentPage.answer}
            </div>
          )}
        </>
      );
    } else if (currentPage.type === "sentence:koreng") {
      return (
        <>
          <div style={{ fontSize: "20px" }}>
            주어진 한국어 문장을 영어로 작문하시오.
          </div>
          <div
            style={{
              fontSize: "28px",
              textAlign: "center",
              lineHeight: "1.5",
            }}
          >
            {currentPage.question}
          </div>
          <div style={{ width: "80%", position: "relative" }}>
            <UserInputBar
              userInput={userInput}
              handleChange={handleChange}
              type={"sentence"}
              handleSetAnswer={handleSubmit}
              onlyUnderline={true}
              isSubmitted={currentPage.isSubmitted}
            />
          </div>
        </>
      );
    } else if (currentPage.type === "sentence:engkor") {
      return (
        <>
          <div style={{ fontSize: "20px" }}>
            주어진 영어 문장을 한국어로 해석하시오.
          </div>
          <div
            style={{
              fontSize: "28px",
              textAlign: "center",
              lineHeight: "1.5",
            }}
          >
            {currentPage.question}
          </div>
          <div style={{ width: "80%", position: "relative" }}>
            <UserInputBar
              userInput={userInput}
              handleChange={handleChange}
              type={"sentence"}
              handleSetAnswer={handleSubmit}
              onlyUnderline={true}
              isSubmitted={currentPage.isSubmitted}
            />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          width: "90%",
        }}
      >
        {renderQuestion()}
      </div>
    </CommonPage>
  );
};

export default ComprehensivePage;
